import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (username, email, password) =>
    api.post('/auth/register', { username, email, password }),

  login: (username, password) =>
    api.post('/auth/login', { username, password }),

  getProfile: () => api.get('/auth/me'),

  updateProfile: (data) => api.put('/auth/profile', data),

  changePassword: (data) => api.put('/auth/password', data),
};

// Tasks API
export const tasksAPI = {
  getTasks: () => api.get('/tasks'),
  
  getTask: (id) => api.get(`/tasks/${id}`),
  
  getApprovedUsers: () => api.get('/tasks/users/approved'),
  
  createTask: (taskData) => {
    const formData = new FormData();
    Object.keys(taskData).forEach(key => {
      if (key === 'attachments' && taskData[key]) {
        taskData[key].forEach(file => {
          formData.append('attachments', file);
        });
      } else if (taskData[key] !== null && taskData[key] !== undefined) {
        formData.append(key, taskData[key]);
      }
    });
    return api.post('/tasks', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  updateTask: (id, taskData) => {
    const formData = new FormData();
    Object.keys(taskData).forEach(key => {
      if (key === 'attachments' && taskData[key]) {
        taskData[key].forEach(file => {
          formData.append('attachments', file);
        });
      } else if (taskData[key] !== null && taskData[key] !== undefined) {
        formData.append(key, taskData[key]);
      }
    });
    return api.put(`/tasks/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  updateTaskStatus: (id, status) =>
    api.put(`/tasks/${id}/status`, { status }),
  
  deleteTask: (id) => api.delete(`/tasks/${id}`),
  
  getAttachments: (id) => api.get(`/tasks/${id}/attachments`),
  
  uploadAttachment: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/tasks/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  deleteAttachment: (taskId, attachmentId) =>
    api.delete(`/tasks/${taskId}/attachments/${attachmentId}`),
  
  downloadAttachment: (taskId, attachmentId) =>
    api.get(`/tasks/${taskId}/attachments/${attachmentId}/download`, {
      responseType: 'blob',
    }),
};

// Task Logs API
export const taskLogsAPI = {
  getLogs: (taskId) => api.get(`/tasks/${taskId}/logs`),
  
  createLog: (taskId, logData) => api.post(`/tasks/${taskId}/logs`, logData),
};

// Admin API
export const adminAPI = {
  // Users
  getUsers: () => api.get('/admin/users'),
  
  getPendingUsers: () => api.get('/admin/users/pending'),
  
  getApprovedUsers: () => api.get('/admin/users/approved'),
  
  approveUser: (id) => api.put(`/admin/users/${id}/approve`),
  
  rejectUser: (id) => api.put(`/admin/users/${id}/reject`),
  
  // Tasks
  getAllTasks: () => api.get('/admin/tasks'),
  
  deleteTask: (id) => api.delete(`/admin/tasks/${id}`),
  
  deactivateTask: (id) => api.put(`/admin/tasks/${id}/deactivate`),
  
  activateTask: (id) => api.put(`/admin/tasks/${id}/activate`),
  
  // Reports
  getStatistics: () => api.get('/admin/reports/statistics'),
  
  getTaskReport: (filters) => api.get('/admin/reports/tasks', { params: filters }),
  
  getUserReport: () => api.get('/admin/reports/users'),
  
  getTimeline: (filters) => api.get('/admin/reports/timeline', { params: filters }),
  
  getTasksResolvedByUser: (filters) => api.get('/admin/reports/tasks-resolved-by-user', { params: filters }),
  
  getTimeSpentByUser: (filters) => api.get('/admin/reports/time-spent-by-user', { params: filters }),
  
  getTimeSpentByTaskType: (filters) => api.get('/admin/reports/time-spent-by-task-type', { params: filters }),
  
  getUserProductivityMetrics: (filters) => api.get('/admin/reports/user-productivity', { params: filters }),
  
  exportReport: (format, filters) =>
    api.get('/admin/reports/export', {
      params: { format, ...filters },
      responseType: 'blob',
    }),
};

export default api;
